import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  MobileInput,
  MobileTextarea,
  MobileSelect,
} from '@/components/ui/widgets';
import { getSitePriorities } from '../types';

interface SiteInfoFormProps {
  siteName: string;
  setSiteName: (value: string) => void;
  currentPriority: number;
  setCurrentPriority: (value: number) => void;
  currentState: string;
  setCurrentState: (value: string) => void;
  currentDescription: string;
  setCurrentDescription: (value: string) => void;
  currentRequiredChanges: string;
  setCurrentRequiredChanges: (value: string) => void;
  updateStatus: {
    siteName: { error: boolean; updating: boolean; updated: boolean };
    priority: { error: boolean; updating: boolean; updated: boolean };
    currentState: { error: boolean; updating: boolean; updated: boolean };
    description: { error: boolean; updating: boolean; updated: boolean };
    requiredChanges: { error: boolean; updating: boolean; updated: boolean };
  };
  onSaveName: () => void;
  onSavePriority: (priority: string) => void;
  onSaveCurrentState: (state: string) => void;
  onSaveDescription: () => void;
  onSaveRequiredChanges: () => void;
}

export const SiteInfoForm: React.FC<SiteInfoFormProps> = ({
  siteName,
  setSiteName,
  currentPriority,
  setCurrentPriority,
  currentState,
  setCurrentState,
  currentDescription,
  setCurrentDescription,
  currentRequiredChanges,
  setCurrentRequiredChanges,
  updateStatus,
  onSaveName,
  onSavePriority,
  onSaveCurrentState,
  onSaveDescription,
  onSaveRequiredChanges,
}) => {
  const { t } = useTranslation();

  const CURRENT_STATE: { value: string; label: string }[] = [
    {
      value: t('current_state_01_select_param'),
      label: 'current_state_01_select_param',
    },
    {
      value: t('current_state_02_select_param'),
      label: 'current_state_02_select_param',
    },
    {
      value: t('current_state_03_select_param'),
      label: 'current_state_03_select_param',
    },
    {
      value: t('current_state_04_select_param'),
      label: 'current_state_04_select_param',
    },
    {
      value: t('current_state_05_select_param'),
      label: 'current_state_05_select_param',
    },
  ];

  // Change handlers
  const onChangeName = (value: string) => {
    setSiteName(value);
  };

  const onChangeCurrentState = (value: string) => {
    setCurrentState(value);
    onSaveCurrentState(value);
  };

  const onChangeDescription = (value: string) => {
    setCurrentDescription(value);
  };

  const onChangeRequiredChanges = (value: string) => {
    setCurrentRequiredChanges(value);
  };

  const onChangePriority = (value: number) => {
    if (value < 1 || value > 10) return;
    setCurrentPriority(value);
    onSavePriority(value.toString());
  };

  return (
    <Box>
      {/* Site Name Input */}
      <Box>
        <MobileInput
          label={`${t('site_name_edit_site')} *`}
          name='siteName'
          error={updateStatus.siteName.error}
          helperText={''}
          compact
          blur={onSaveName}
          onChange={value => onChangeName(value.target.value)}
          value={siteName}
          disabled={updateStatus.siteName.updating}
          updated={updateStatus.siteName.updated}
        />
      </Box>

      {/* Priority and Current State Row */}
      <Box sx={{ display: 'flex', gap: '1.25rem', marginTop: '1.25rem' }}>
        <Box
          width='calc(30% - 0.625rem)'
          minWidth='calc(30% - 0.625rem)'
          maxWidth={'calc(30% - 0.625rem)'}
        >
          <MobileSelect
            title={t('priority_edit_site')}
            items={getSitePriorities(t)}
            error={updateStatus.priority.error}
            noSelectLabel={t('priority_placeholder_edit_site')}
            onChange={value => onChangePriority(parseInt(value))}
            value={currentPriority.toString()}
            disabled={updateStatus.priority.updating}
            updated={updateStatus.priority.updated}
          />
        </Box>
        <Box
          width='calc(70% - 0.625rem)'
          minWidth='calc(70% - 0.625rem)'
          maxWidth={'calc(70% - 0.625rem)'}
        >
          <MobileSelect
            title={t('current_state_edit_site')}
            items={CURRENT_STATE}
            error={updateStatus.currentState.error}
            noSelectLabel={t('current_state_placeholder_edit_site')}
            onChange={value => onChangeCurrentState(value)}
            value={currentState}
            disabled={updateStatus.currentState.updating}
            updated={updateStatus.currentState.updated}
            translate
          />
        </Box>
      </Box>

      {/* Required Changes Input */}
      <MobileInput
        label={`${t('required_changes_edit_site')}`}
        name='requiredChanges'
        error={updateStatus.requiredChanges.error}
        helperText={''}
        onChange={value => onChangeRequiredChanges(value.target.value)}
        value={currentRequiredChanges}
        autoHeight
        blur={onSaveRequiredChanges}
        disabled={updateStatus.requiredChanges.updating}
        updated={updateStatus.requiredChanges.updated}
      />

      {/* Description Textarea */}
      <MobileTextarea
        label={`${t('description_edit_site')}`}
        name='description'
        error={updateStatus.description.error}
        helperText={''}
        onChange={value => onChangeDescription(value.target.value)}
        value={currentDescription}
        blur={onSaveDescription}
        disabled={updateStatus.description.updating}
        updated={updateStatus.description.updated}
      />
    </Box>
  );
};
